import {
  IHRDatasetInfo,
  IHRSettings,
} from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { EventBus } from 'core-components/classes/events/Bus'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { HttpCancel } from 'core/classes/http'
import Logger from 'core/utils/Logger'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { fetchEltPlanningData } from 'partner360/services/planning/fetchEltPlanningData'

import {
  changeActualsType,
  changeCurrency,
  setAvailableActualsTypes,
  setAvailableCurrencies,
} from '../../../../../../../features/partner360/configuration'
import { IJBPPlanning } from '../../../models/IJBPPlanning'
import { WidgetHeader } from '../../../widgetHeader/WidgetHeader'
import { fetchEltPlanningSettings } from '../../api/fetchEltPlanningSettings'
import { CategoryPlanning } from '../../CategoryPlanning'
import { IS_ALWAYS_SHOW_ACTUALS, PlanningRounding } from '../../config'
import { isFutureYearsPlansPresent } from '../../helpers/isFutureYearsPlansPresent'
import { TSplitType } from '../../models'
import tr from '../../translations'
import { useFocusOnKeyPress } from '../floatingNumberInput'

import styles from './EltPlanning.module.scss'

interface IProps {
  uid?: string
  widgetParams: IObjectAny
  metrics: MeasureController
  fYear: number
  netNdp: string
  eventBus: EventBus
}

export const EltPlanning = (props: IProps) => {
  /** -------------------- State ----------------------- */

  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [hrDataSet, setHRDataSet] = useState<IHRDatasetInfo>()
  const [hrDataSets, setHRDataSets] = useState<IHRDatasetInfo[]>([])
  const [hrFormats, setHRFormats] = useState<IFormActualsTargetsSettings[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [isNoData, setIsNoData] = useState(false)
  const [isRegionalPlanning, setIsRegionalPlanning] = useState(true)
  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false)
  const [isShowActuals, setIsShowActuals] = useState(false)
  const [isShowFutureYearsPlans, setIsShowFutureYearsPlans] = useState(false)
  const [isShowQuota, setIsShowQuota] = useState(false)
  const [planningData, setPlanningData] = useState<IJBPPlanning[]>([])
  const [originalActualsValueType, setOriginalActualsValueType] = useState('')
  const [splitType, setSplitType] = useState<TSplitType>('Total')

  const [roundingValue, setRoundingValue] = useState(PlanningRounding.AsIs)
  const [isShowPrevTargets, setIsShowPrevTargets] = useState(true)
  const [isShowValidationPopup, setIsShowValidationPopup] = useState(false)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const { startLoadData, stopLoadData } = useMeasureController(props.metrics)
  const t = useSafetyTranslation()
  const onKeyPress = useFocusOnKeyPress()

  /** -------------------- Variables ----------------------- */

  const isShowLoader = !isSettingsLoaded || !isDataLoaded
  const isShowCancelRequest = isInitialized && isShowLoader
  const isHideWidget = !isInitialized

  // Display settings for ELT mode
  const displaySettings = useMemo(
    () => ({
      settings: {
        isNewLookAndFeel: true,
        isYearlyPlanning: false,
        isShowDetailedPlanning: false,

        isShowPreviousPlans: isShowPrevTargets,
        isShowPlanAchievement: true,
        isShowQuotaTargets: isShowQuota,
        isShowQuotaAchievement: false,
        isShowActualsSplit: false,
        isShowSmbVsEuSplit: false,
        isShowYoYActualsVsActuals: true,
        isShowYoYActualsVsPlans: true,
        isShowYoYPlansVsPlans: false,
        isShowYoYQuotaVsActuals: false,
        isShowYoYPartnersVsPartners: false,
        isShowQoQActualsVsActuals: false,
        isShowQoQActualsVsPlans: false,
        isShowQoQPlansVsPlans: false,
        isShowQoQQuotaVsActuals: false,
        isShowQoQPartnersVsPartners: false,
        isShowPartnerCount: false,
      },
      actions: {
        setIsNewLookAndFeel: () => {},
        setIsYearlyPlanning: () => {},
        setIsShowDetailedPlanning: () => {},

        setIsShowPreviousPlans: setIsShowPrevTargets,
        setIsShowPlanAchievement: () => {},
        setIsShowQuotaTargets: setIsShowQuota,
        setIsShowQuotaAchievement: () => {},
        setIsShowActualsSplit: () => {},
        setIsShowSmbVsEuSplit: () => {},
        setIsShowYoYActualsVsActuals: () => {},
        setIsShowYoYActualsVsPlans: () => {},
        setIsShowYoYPlansVsPlans: () => {},
        setIsShowYoYQuotaVsActuals: () => {},
        setIsShowYoYPartnersVsPartners: () => {},
        setIsShowQoQActualsVsActuals: () => {},
        setIsShowQoQActualsVsPlans: () => {},
        setIsShowQoQPlansVsPlans: () => {},
        setIsShowQoQQuotaVsActuals: () => {},
        setIsShowQoQPartnersVsPartners: () => {},
        setIsShowPartnerCount: () => {},
        resetToDefaults: () => {},
        updateSettings: () => {},
      },
    }),
    [isShowPrevTargets, isShowQuota, setIsShowPrevTargets, setIsShowQuota]
  )

  /** -------------------- Methods ----------------------- */

  const fetchPlanningSettings = async (cancelObject?: HttpCancel) => {
    setIsSettingsLoaded(false)

    let settingsData: IFormActualsTargetsSettings[] = []
    if (props.uid) {
      const settingsResponse = await fetchEltPlanningSettings<IFormActualsTargetsSettings>(
        props.uid,
        cancelObject
      )
      settingsData = (settingsResponse?.Data || []).map((el) => ({
        ...el,
        ProgramTypes: ProgramTypes.PersonalSystems,
      }))
    }

    setIsNoData(!settingsData.length)

    if (settingsData.length) {
      setHRFormats(settingsData)

      let category = ''

      const categoriesSettings: IHRDatasetInfo[] = []
      settingsData.forEach((el) => {
        let settings: IHRSettings = {}
        try {
          settings = JSON.parse(el.Settings)
        } catch {
          Logger.error('Fail to parse Planning settings at ELT planning')
        }

        if (
          category !== el.Category &&
          !categoriesSettings.find((hi) => hi.category === el.Category)
        ) {
          category = el.Category

          // Temporally hide MPS Summary Chart
          if (!el.SummaryProgram) {
            categoriesSettings.push({
              category: category,
              categoryTranslation: t(el.Category),
              subCategory: el.SubCategory,
              colors: (el.Colors || '').split(';'),
              ProgramType: el.ProgramType,
              SummaryProgram: !!el.SummaryProgram,
              Settings: settings,
              IsContractualsAvailable: el.IsContractualsAvailable,
              IsSoBAvailable: el.IsSoBAvailable,
              IsSoBTotalCalc: el.IsSoBTotalCalc,
              IsUnitsAvailable: el.IsUnitsAvailable,
              IsHideFinancial: el.IsHideFinancial,
              IsOnlyQuarterlyPlanning: el.IsOnlyQuarterlyPlanning,
              IsMainHierarchy: el.IsMainHierarchy,
              MandatoryType: el.MandatoryType,
              MandatoryByFlags: el.MandatoryByFlags,
              UnitsMandatoryByFlags: el.UnitsMandatoryByFlags,
            })
          }
        }
      })
      setHRDataSets(categoriesSettings)
      if (categoriesSettings && categoriesSettings.length) {
        setHRDataSet(categoriesSettings[0])
      }
    }

    setIsSettingsLoaded(true)
  }

  const setOriginalSwitcherValues = (allPlanningData: IJBPPlanning[]) => {
    // Set Currency and ActualsValueType by first Targets data row
    const targetData = allPlanningData.filter((el) => !!el.IsTarget)
    if (targetData.length) {
      if (targetData[0].Currency) {
        //setCurrency(targetData[0].Currency)
        //setOriginalCurrency(targetData[0].Currency)
      }
      if (targetData[0].NetNdp) {
        //setActualsValueType(targetData[0].NetNdp)
        setOriginalActualsValueType(targetData[0].NetNdp)
      }
    }
  }

  /** -------------------- User actions handlers ----------------------- */

  const updatePlanning = async (forceUpdate: boolean = false) => {
    setIsDataLoaded(false)
    setIsInitialized(true)
    startLoadData()

    setHRDataSets([])
    setHRDataSet(undefined)
    setHRFormats([])

    fetchPlanningSettings(cancelObject)

    if (props.uid) {
      const planningResult = await fetchEltPlanningData<IJBPPlanning>(
        props.uid,
        forceUpdate,
        cancelObject
      )
      const allPlanningData = planningResult?.Data || []
      setIsNoData(!allPlanningData.length)
      setPlanningData(allPlanningData)
      setOriginalSwitcherValues(allPlanningData)
      const isFutureDataPresent = isFutureYearsPlansPresent(allPlanningData, props.fYear)
      setIsShowFutureYearsPlans(isFutureDataPresent)
    }

    stopLoadData()
    setIsDataLoaded(true)
  }

  const onApplyChanges = (data: IJBPPlanning[]) => {
    const allPlanningData = data || []
    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)
  }

  const cancelDataRequest = () => {
    if (cancelObject) {
      cancelObject.cancel()
    }
  }

  /** -------------------- Effects ----------------------- */

  const cancelObject = useMemo(() => new HttpCancel(), [])

  useEffect(() => {
    const onEnterPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        onKeyPress()
      }
    }

    document.addEventListener('keypress', onEnterPress)
    return () => {
      document.removeEventListener('keypress', onEnterPress)
    }
  }, [onKeyPress])

  useEffect(() => {
    // init netNdp
    dispatch(setAvailableActualsTypes([props.netNdp as ActualsValueTypes]))
    dispatch(changeActualsType(props.netNdp as ActualsValueTypes))
    // init currency
    dispatch(setAvailableCurrencies(['USD']))
    dispatch(changeCurrency('USD'))
  }, [dispatch, props.netNdp])

  useEffect(() => {
    if (props.uid) {
      updatePlanning()
    }
  }, [props.uid])

  useEffect(() => {
    const unsubscribe = props.eventBus.subscribe('updateScoreCardData', () => {
      if (props.uid) {
        updatePlanning(true)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [props.uid])
  /** -------------------- Render ----------------------- */

  if (isHideWidget) {
    return <></>
  }

  if (isNoData) {
    return <WidgetHeader>{tr.noDataWarning}</WidgetHeader>
  }

  return (
    <div className={styles.root}>
      {isShowCancelRequest && (
        <Button onClick={cancelDataRequest}>
          <Translation text="jbp-cancel-request" span />
        </Button>
      )}

      <div className={styles.root}>
        <LoaderOverlay show={isShowLoader} />
        {!!hrDataSet && (
          <CategoryPlanning
            key={`jbp-planning-${hrDataSet?.category}-total`}
            {...props}
            pid=""
            planningData={planningData}
            mapData={[]}
            dataSetInfo={hrDataSet}
            allDataSets={hrDataSets}
            formats={hrFormats.filter((el) => el.Category === hrDataSet?.category)}
            displaySettings={displaySettings}
            showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
            setShowActuals={(show) => setIsShowActuals(show)}
            showQuota={isShowQuota}
            setShowQuota={(show) => setIsShowQuota(show)}
            isShowFutureYearsPlans={isShowFutureYearsPlans}
            isRegionalPlanning={isRegionalPlanning}
            setIsRegionalPlanning={(isRegionalPlanning) =>
              setIsRegionalPlanning(isRegionalPlanning)
            }
            isContractualPlanning={false}
            isShowOnlyCompletedPlans={false}
            onApply={(data) => onApplyChanges(data)}
            originalCurrency={'USD'}
            originalActualsValueType={originalActualsValueType}
            partnerFlagsState={[]}
            isQuarterlyBusinessReviewMode
            quarterlyBusinessReviewFYear={props.fYear}
            quarterlyBusinessReviewNetNdp={props.netNdp}
            quarterlyBusinessReviewCurrency={'USD'}
            splitType={splitType}
            setSplitType={setSplitType}
            isDistributorPlanner={false}
            roundingValue={roundingValue}
            setRoundingValue={setRoundingValue}
            roundingValueOriginal={PlanningRounding.AsIs}
            isEltMode
            isShowValidationPopup={isShowValidationPopup}
            setIsShowValidationPopup={setIsShowValidationPopup}
          />
        )}
      </div>
    </div>
  )
}
